import React, { ReactNode, useEffect } from 'react'
import {
  Box,
  CarouselControls,
  CarouselItem,
  CarouselNextButton,
  CarouselPreviousButton,
  CarouselRoot,
  CarouselScrollArea,
  CarouselState,
  Heading,
  Text,
} from '@moonpig/launchpad-components'
import { useTrackGAEventOnce } from '@moonpig/web-core-analytics'
import { useInView } from 'react-intersection-observer'
import { useStoreId } from '@moonpig/web-core-stores'
import { Markup } from '@moonpig/web-shared-components'
import { styled } from '@moonpig/launchpad-utils'
import { system as s } from '@moonpig/launchpad-system'
import { ProductTileWithBasketHandler } from './ProductTileWithBasketHandler'
import { createViewItemListGAEvent } from '../../analytics'
import { ProductTileProduct } from '../ProductTile'
import { AddedToBasketEvent, ProductList } from '../../types'

const isPurchasableProduct = (product: ProductTileProduct | null): boolean =>
  product !== null && product.masterVariant.inStock

const StyledCarouselScrollArea = styled(CarouselScrollArea)`
  ${s({ pt: 6 })}
`

const CarouselRootWithoutPadding = styled(CarouselRoot)`
  ${s({ px: { xs: 0, lg: 6 } })}
`

const DesktopCarouselControls = styled(CarouselControls)`
  ${s({
    display: { xs: 'none', lg: 'flex' },
  })}
`

export const RecommendationsCarousel: React.FC<{
  heading?: string
  subHeading?: string
  headingAlignment?: 'center' | 'left'
  pageName: string
  products: (ProductTileProduct | null)[]
  showOurPick?: boolean
  addToBasketSuccessCallback?: (e: AddedToBasketEvent) => void
  seeMoreComponent?: ReactNode
  carouselLength: number | string
  carouselIndex: number | string
  showSeeMoreAboveCarousel?: boolean
}> = ({
  heading,
  pageName,
  products,
  subHeading,
  headingAlignment,
  showOurPick,
  addToBasketSuccessCallback,
  seeMoreComponent,
  carouselLength,
  carouselIndex,
  showSeeMoreAboveCarousel,
}) => {
  const { trackGAEventOnce } = useTrackGAEventOnce()
  const storeId = useStoreId()
  const [productListRef, inView] = useInView({
    threshold: 0.8,
    triggerOnce: true,
  })

  const purchasableProducts = products.filter(
    isPurchasableProduct,
  ) as ProductTileProduct[]
  const productListProps: ProductList = {
    title: heading || subHeading || '',
    position: carouselIndex,
    totalNumberOfLists: carouselLength,
  }

  useEffect(() => {
    const headingToTrack = heading?.replace(/(<([^>]+)>)/gi, '')

    if (inView && purchasableProducts.length) {
      const label = `${pageName} ${
        headingToTrack ? `| ${headingToTrack}` : ''
      } | carousel | ${carouselIndex}/${carouselLength}`
      trackGAEventOnce(
        createViewItemListGAEvent({
          products: purchasableProducts,
          label,
        }),
      )
    }
  }, [
    inView,
    pageName,
    heading,
    storeId,
    carouselIndex,
    carouselLength,
    purchasableProducts,
    trackGAEventOnce,
  ])

  if (purchasableProducts.length === 0) {
    return null
  }

  return (
    <Box
      ref={productListRef}
      mb={10}
      width={1}
      data-testid="recommendations-carousel"
    >
      <Box className="recommendations-carousel-header-wrapper">
        <Box className="recommendations-carousel-heading-wrapper">
          {heading && (
            <Heading
              level="h2"
              data-testid="heading"
              textAlign={headingAlignment}
              mb={subHeading ? 0 : undefined}
              px={6}
              typography={{
                xs: 'typeMobileDisplay03',
                md: 'typeDesktopDisplay04',
              }}
            >
              <Markup htmlString={heading} />
            </Heading>
          )}
          {subHeading && (
            <Box px={6}>
              <Text
                typography="typeBodyCaption"
                as="p"
                data-testid="sub-heading"
                textAlign={headingAlignment}
                mb={0}
              >
                {subHeading}
              </Text>
            </Box>
          )}
        </Box>
        {showSeeMoreAboveCarousel && seeMoreComponent}
      </Box>
      <CarouselState>
        <CarouselRootWithoutPadding
          aria-label={`${heading} Carousel`}
          data-testid="lp-components-carousel"
        >
          <DesktopCarouselControls>
            <CarouselPreviousButton />
            <CarouselNextButton />
          </DesktopCarouselControls>
          <StyledCarouselScrollArea>
            {purchasableProducts.map((product, index) => {
              return (
                <CarouselItem key={product.id} style={{ flex: '0 1 auto' }}>
                  <Box
                    minWidth={{ xs: '13rem', md: '16rem' }}
                    ml={{ xs: index === 0 ? 5 : 0 }}
                    mr={{
                      xs: index === purchasableProducts.length - 1 ? 5 : 0,
                    }}
                    pt={{ xs: 6, md: 5 }}
                    paddingBottom={10}
                    height="100%"
                  >
                    <ProductTileWithBasketHandler
                      product={product}
                      productsLength={purchasableProducts.length}
                      index={index}
                      pageName={pageName}
                      showProductTabs
                      hideStickyCta
                      showOurPick={showOurPick}
                      productList={productListProps}
                      addToBasketSuccessCallback={addToBasketSuccessCallback}
                    />
                  </Box>
                </CarouselItem>
              )
            })}
          </StyledCarouselScrollArea>
        </CarouselRootWithoutPadding>
      </CarouselState>
      {!showSeeMoreAboveCarousel && (
        <Box px={6} my={0}>
          {seeMoreComponent}
        </Box>
      )}
    </Box>
  )
}
